import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  filterLocations as filter,
  isPendingOpportunity,
  sortByNumber,
  sortByName,
  sortByCity,
  sortByState,
  sortByRegion,
  sortBySelectionTimeline,
  sortBySelectionDate,
} from "../../utils";

import type { RootState } from "../store";
import type Location from "../../types/Location";
import { getLocations } from "../../api/CfaApi";

export const fetchLocations = createAsyncThunk("locations/fetch", async () => {
  return await getLocations();
});

export interface LocationsState {
  opportunities: Location[]; // all pending opportunities
  displayed: string[]; // IDs of pending opportunities to be displayed
  favoritesList: string[]; // IDs of favorited opportunities to be displayed
  showFavoritedLocations: boolean;
  active?: string; // the currently selected location
  loading: boolean;
  error?: string;
}

const initialState: LocationsState = {
  opportunities: [],
  displayed: [],
  favoritesList: [],
  showFavoritedLocations: false,
  loading: false,
};

const slice = createSlice({
  name: "locations",
  initialState,
  reducers: {
    filterLocations: (
      state,
      action: PayloadAction<{ q: string; regions: string[]; states: string[] }>,
    ) => {
      if (!state.opportunities) return;

      const { q, regions, states } = action.payload;
      const filtered = filter(state.opportunities, q, regions, states);
      state.displayed = filtered.map((l) => l.locationNumber);
    },
    setActiveLocation: (state, action: PayloadAction<string | undefined>) => {
      state.active = action.payload;
    },
    sortLocations: (
      state,
      action: PayloadAction<{ sort: string; order: "asc" | "desc" }>,
    ) => {
      const { sort, order } = action.payload;

      const functionMap: {
        [key: string]: (a: Location, b: Location) => number;
      } = {
        number: sortByNumber,
        name: sortByName,
        city: sortByCity,
        state: sortByState,
        region: sortByRegion,
        timeline: sortBySelectionTimeline,
        date: sortBySelectionDate,
      };

      const sortFunction = functionMap[sort];

      state.displayed = state.opportunities
        .filter((l) => state.displayed.includes(l.locationNumber))
        .sort(sortFunction)
        .map((l) => l.locationNumber);
      if (order === "desc") state.displayed.reverse();
    },
    addFavorite: (state, action: PayloadAction<string>) => {
      const locationId: string = action.payload;
      const index = state.favoritesList.indexOf(locationId);
      if (index > -1) {
        return;
      }
      state.favoritesList.push(locationId);

      localStorage.setItem("favoritedList", state.favoritesList.join(","));
    },
    removeFavorite: (state, action: PayloadAction<string>) => {
      const locationId: string = action.payload;
      const index = state.favoritesList.indexOf(locationId);
      if (index > -1) {
        state.favoritesList.splice(index, 1);
      } else {
        return;
      }
      localStorage.setItem("favoritedList", state.favoritesList.join(","));
    },
    fetchFavoritesList: (state, action: PayloadAction<string | undefined>) => {
      const list = localStorage.getItem("favoritedList");
      state.favoritesList = !list ? [] : list.split(","); //favoritedList will be set to empty array or array of locationId's
    },
    setShowFavoritedLocations: (state, action: PayloadAction<boolean>) => {
      state.showFavoritedLocations = action.payload;
    },
  },
  extraReducers: (builder) => {
    // how to test these?
    builder.addCase(fetchLocations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchLocations.fulfilled, (state, action) => {
      state.loading = false;
      state.opportunities =
        action.payload.locationList.filter(isPendingOpportunity);
    });
    builder.addCase(fetchLocations.rejected, (state) => {
      // error throw will trigger this case
      state.loading = false;
      state.error = "Failed to fetch locations.";
    });
  },
});

export const selectActiveLocation = createSelector(
  [
    (state: RootState) => state.locations.opportunities,
    (state: RootState) => state.locations.active,
  ],
  (locations, id) =>
    id ? locations.find((l) => l.locationNumber === id) : undefined,
);

export const selectAllDisplayLocations = createSelector(
  [
    (state: RootState) => state.locations.opportunities,
    (state: RootState) => state.locations.displayed,
  ],
  (locations, ids) =>
    ids.map((id) => locations.find((l) => l.locationNumber === id)!),
);

export const selectFavoritedDisplayLocations = createSelector(
  [
    (state: RootState) => state.locations.opportunities,
    (state: RootState) => state.locations.displayed,
    (state: RootState) => state.locations.favoritesList,
  ],
  (locations, displayedIds, favoritedIds) =>
    displayedIds
      .filter((id) => favoritedIds.includes(id))
      .flatMap((id) => {
        const location = locations.find((l) => l.locationNumber === id)!;
        return location || [];
      }),
);

export const selectFavoritesList = (state: RootState) =>
  state.locations.favoritesList;

export const selectShowFavoritedLocations = (state: RootState) =>
  state.locations.showFavoritedLocations;

export const {
  filterLocations,
  setActiveLocation,
  sortLocations,
  addFavorite,
  removeFavorite,
  fetchFavoritesList,
  setShowFavoritedLocations,
} = slice.actions;

export default slice.reducer;
